import React, { useEffect, Suspense, lazy } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, MapPinIcon, ArrowUpRightIcon, MailIcon, StarIcon, CheckIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { SISTER_ORGANIZATIONS } from '../data/sister-organizations';
import { PILLARS, PARTICIPATION, JOIN_STEPS, PRESIDENT_LETTER } from '../data/national-network-content';
const NetworkMap = lazy(() => import('../components/NetworkMap').then(m => ({
  default: m.NetworkMap
})));
const NETWORK_LOGO = 'https://cdn.magicpatterns.com/uploads/eZ5rmta1LUT5zNjEpkMWnd/HAA_National_Network.png';
const CONTACT_EMAIL = 'eva@honduranamerican.org';
const NAVY = '#0b1f3f';
const NAVY_DEEP = '#071427';
const SKY = '#3FA9F5';
const RED = '#e0343c';
const EASE = [0.16, 1, 0.3, 1] as const;
function SectionLabel({
  children
}: {
  children: React.ReactNode;
}) {
  return <p className="inline-flex items-center gap-2.5 font-nunito text-[10px] font-bold uppercase tracking-[0.3em] mb-5" style={{
    color: SKY
  }} data-id="element-3786" data-mp="vc4wnafr7l63">
      <StarIcon className="w-3 h-3" style={{
      color: RED
    }} fill={RED} data-id="element-3787" data-mp="2gz26993jkn4j" />
      {children}
    </p>;
}
export function NationalNetworkPage() {
  const {
    language,
    setLanguage
  } = useLanguage();
  const isEs = language === 'ES';
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const mailto = (subject: string) => `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`;
  const joinHref = mailto(isEs ? 'Solicitud de Membresía — Red Nacional Hondureño-Americana' : 'Membership Application — Honduran-American National Network');
  return <div className="min-h-screen w-full text-white font-nunito antialiased" style={{
    backgroundColor: NAVY
  }} data-id="element-3788" data-mp="1p7mdexbe645v">
      {/* Header */}
      <header className="sticky top-0 z-30 w-full backdrop-blur-md border-b border-white/10" style={{
      backgroundColor: 'rgba(7,20,39,0.92)'
    }} data-id="element-3789" data-mp="3nlxsrosd0zz2">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3.5 flex items-center justify-between" data-id="element-3790" data-mp="1gygmylbtdjfj">
          <a href="#" className="flex items-center gap-3 group" aria-label="Honduran American Association National Network" data-id="element-3791" data-mp="1lq8f30ambx7s">
            <span className="inline-flex bg-white rounded-md px-2.5 py-1.5" data-id="element-3792" data-mp="17wh5jrww0dc2">
              <img src={NETWORK_LOGO} alt="Honduran American Association National Network" className="h-9 sm:h-10 w-auto object-contain group-hover:opacity-80 transition-opacity" data-id="element-3793" data-mp="1c05b651ykyuo" />
            </span>
          </a>
          <div className="flex items-center gap-5" data-id="element-3794" data-mp="1qnqi6a1yfi7v">
            <div className="inline-flex rounded-full border border-white/25 overflow-hidden" role="group" aria-label={isEs ? 'Idioma' : 'Language'} data-id="element-3795" data-mp="1vnuxhwr4m6wi">
              {(['EN', 'ES'] as const).map(lang => <button key={lang} type="button" onClick={() => setLanguage(lang)} aria-pressed={language === lang} className="px-3.5 py-1.5 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] transition-colors" style={{
              backgroundColor: language === lang ? SKY : 'transparent',
              color: language === lang ? '#ffffff' : 'rgba(255,255,255,0.6)'
            }} data-id="element-3796" data-mp="kaan0bpu7f7l">
                  {lang}
                </button>)}
            </div>
            <a href={joinHref} className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-[11px] font-bold uppercase tracking-[0.16em] transition-colors" style={{
            backgroundColor: SKY
          }} onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = RED;
          }} onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = SKY;
          }} data-id="element-3797" data-mp="3k5o00gosnzyx">
              {isEs ? 'Únase' : 'Join'}
            </a>
            <a href="#" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-xs font-semibold uppercase tracking-[0.16em] transition-colors" data-id="element-3798" data-mp="29pifaw18ernc">
              <ArrowLeftIcon className="w-3.5 h-3.5" data-id="element-3799" data-mp="2iilqexoemset" />
              {isEs ? 'Volver' : 'Back'}
            </a>
          </div>
        </div>
        <div className="h-[3px] w-full flex" data-id="element-3800" data-mp="2sf8a3j4zvdf9">
          <div className="flex-1" style={{
          backgroundColor: SKY
        }} data-id="element-3801" data-mp="2sdx5xi7n03nm" />
          <div className="flex-1 bg-white" data-id="element-3802" data-mp="2xdrwtlrewt8o" />
          <div className="flex-1" style={{
          backgroundColor: RED
        }} data-id="element-3803" data-mp="2xet7vq8fhfqe" />
        </div>
      </header>

      <main data-id="element-3804" data-mp="31fd6ky8ngren">
        {/* 1 — Hero */}
        <section className="relative overflow-hidden" style={{
        backgroundColor: NAVY_DEEP
      }} data-id="element-3805" data-mp="130a7bt7g5mb0">
          <div aria-hidden="true" className="absolute inset-0 opacity-[0.13]" style={{
          backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.2) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.2) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(ellipse at 50% 40%, black 20%, transparent 74%)',
          WebkitMaskImage: 'radial-gradient(ellipse at 50% 40%, black 20%, transparent 74%)'
        }} data-id="element-3806" data-mp="171ca2k0koghq" />
          <div className="relative max-w-5xl mx-auto px-5 sm:px-8 pt-16 pb-16 lg:pt-20 lg:pb-20 text-center" data-id="element-3807" data-mp="1dhvx92q9xs8k">
            <motion.div initial={{
            opacity: 0,
            y: 18
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: EASE
          }} data-id="element-3808" data-mp="19fhai7jhi8c0">
              <span className="inline-flex bg-white rounded-2xl px-7 py-5 mb-10" data-id="element-3809" data-mp="3u71c84f0rwbf">
                <img src={NETWORK_LOGO} alt="Honduran American Association National Network" className="w-[clamp(15rem,40vw,26rem)] h-auto object-contain" data-id="element-3810" data-mp="3p759s9lz0cxz" />
              </span>
              <h1 className="font-bebas text-4xl sm:text-5xl lg:text-6xl tracking-[0.03em] leading-[1.02] mb-8" data-id="element-3811" data-mp="3vnexme4qm56g">
                <span className="block" style={{
                color: SKY
              }} data-id="element-3812" data-mp="3hq8daf4d7yf4">
                  {isEs ? 'Organizaciones Más Fuertes' : 'Stronger Organizations'}
                </span>
                <span className="block text-white" data-id="element-3813" data-mp="37xkkdvgiaelo">
                  {isEs ? 'Mayor Conexión' : 'Greater Connection'}
                </span>
                <span className="block" style={{
                color: RED
              }} data-id="element-3814" data-mp="3cqvq1t9l0pc0">
                  {isEs ? '¡Impacto Binacional!' : 'Bi-National Impact!'}
                </span>
              </h1>
              <p className="font-nunito text-white/70 text-lg leading-relaxed max-w-2xl mx-auto mb-9" data-id="element-3815" data-mp="32zj41iagstdw">
                {isEs ? 'Un espacio nacional que conecta a las organizaciones, líderes y socios que ya sirven a las comunidades hondureñas en los Estados Unidos y en Honduras.' : 'A national space connecting the organizations, leaders, and partners already serving Honduran communities in the United States and in Honduras.'}
              </p>
              <a href={joinHref} className="group inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-white font-bold text-xs uppercase tracking-[0.16em] transition-colors" style={{
              color: NAVY
            }} onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = RED;
              e.currentTarget.style.color = '#ffffff';
            }} onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = '#ffffff';
              e.currentTarget.style.color = NAVY;
            }} data-id="element-3816" data-mp="2jf0ziqqnbs8t">
                {isEs ? 'Únase a la Red' : 'Join the Network'}
                <ArrowUpRightIcon className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" data-id="element-3817" data-mp="374d3nc749a49" />
              </a>
            </motion.div>
          </div>
        </section>

        {/* 2 — Letter from the President */}
        <section className="border-y border-white/10" style={{
        backgroundColor: NAVY
      }} data-id="element-3818" data-mp="ra9yu4dtar4u">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20 lg:py-24" data-id="element-3819" data-mp="w8tks4seo2x7">
            <SectionLabel data-id="element-3820" data-mp="3vp9nxfvhs5o9">
              {isEs ? 'Carta del Presidente' : 'Letter from the President'}
            </SectionLabel>
            <p className="font-bebas text-3xl tracking-[0.04em] mb-7" style={{
            color: SKY
          }} data-id="element-3821" data-mp="3m8rgysddgr0">
              {isEs ? PRESIDENT_LETTER.salutationEs : PRESIDENT_LETTER.salutation}
            </p>
            <div className="space-y-5 font-nunito text-white/75 text-base sm:text-[1.0625rem] leading-relaxed" data-id="element-3822" data-mp="dd3jkjsarakd">
              {(isEs ? PRESIDENT_LETTER.paragraphsEs : PRESIDENT_LETTER.paragraphs).map(p => <p key={p.slice(0, 40)} data-id="element-3823" data-mp="39fjikit4e99p">{p}</p>)}
            </div>
            <div className="mt-10 pt-7 border-t border-white/15" data-id="element-3824" data-mp="34gzvgf2sirfr">
              <p className="font-nunito text-white/55 text-sm mb-4" data-id="element-3825" data-mp="34foqibxlzdn2">
                {isEs ? PRESIDENT_LETTER.closingEs : PRESIDENT_LETTER.closing}
              </p>
              <p className="font-bebas text-2xl tracking-[0.04em] text-white" data-id="element-3826" data-mp="2vga0tep9n73m">
                {PRESIDENT_LETTER.name}
              </p>
              <p className="font-nunito text-white/55 text-sm" data-id="element-3827" data-mp="2qjjisltwi1fk">
                {isEs ? PRESIDENT_LETTER.roleEs : PRESIDENT_LETTER.role}
              </p>
            </div>
          </div>
        </section>

        {/* 3 — Pillars of Collaboration */}
        <section style={{
        backgroundColor: NAVY_DEEP
      }} data-id="element-3828" data-mp="2btvnc18ehwzr">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-id="element-3829" data-mp="27r8wo3ngui45">
            <div className="text-center max-w-2xl mx-auto mb-14" data-id="element-3830" data-mp="27t1x317hk63p">
              <SectionLabel data-id="element-3831" data-mp="22snysavosaiv">{isEs ? '01 — La Red' : '01 — The Network'}</SectionLabel>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95]" data-id="element-3832" data-mp="hodp0u43egzf">
                {isEs ? 'Pilares de Colaboración' : 'Pillars of Collaboration'}
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12" data-id="element-3833" data-mp="rep339hhq6k1">
              {PILLARS.map((pillar, i) => {
              const Icon = pillar.icon;
              return <motion.article key={pillar.title} initial={{
                opacity: 0,
                y: 16
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-60px'
              }} transition={{
                duration: 0.45,
                delay: Math.min(i, 3) * 0.06,
                ease: EASE
              }} className="relative pl-14" data-id="element-3834" data-mp="3uwh0j3eebipm" data-mpi={i}>
                    <span className="absolute left-0 top-0 w-9 h-9 rounded-full flex items-center justify-center font-bebas text-base tracking-wide text-white" style={{
                  backgroundColor: i % 2 === 0 ? SKY : 'rgba(255,255,255,0.12)'
                }} aria-hidden="true" data-id="element-3835" data-mp="1f41ac3t9c4jp">
                      {i + 1}
                    </span>
                    <Icon className="w-6 h-6 mb-4" style={{
                  color: SKY
                }} strokeWidth={1.6} data-id="element-3836" data-mp="25etkdx8bf57m" />
                    <h3 className="font-bebas text-2xl tracking-[0.04em] leading-tight mb-3" data-id="element-3837" data-mp="2ag0s5wagk4ea">
                      {isEs ? pillar.titleEs : pillar.title}
                    </h3>
                    <p className="font-nunito text-white/65 text-sm leading-relaxed" data-id="element-3838" data-mp="20mjxyr6esna3">
                      {isEs ? pillar.textEs : pillar.text}
                    </p>
                  </motion.article>;
            })}
            </div>
          </div>
        </section>

        {/* 4 — Year One participation */}
        <section className="border-y border-white/10" style={{
        backgroundColor: NAVY
      }} data-id="element-3839" data-mp="32y1wiyckxofb">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-id="element-3840" data-mp="2tzg5eet1l12x">
            <div className="max-w-2xl mb-14" data-id="element-3841" data-mp="2u1j5xsrtmclw">
              <SectionLabel data-id="element-3842" data-mp="2ozk1sgo9bbnw">
                {isEs ? '02 — Participación' : '02 — Participation'}
              </SectionLabel>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95] mb-6" data-id="element-3843" data-mp="2k2tkjq0pua0w">
                {isEs ? 'Cómo Se Ve la Participación en el Primer Año' : 'What Participation Can Look Like in Year One'}
              </h2>
              <p className="font-nunito text-base leading-relaxed text-white/70" data-id="element-3844" data-mp="26bf1xkaq0aud">
                {isEs ? 'Construyendo conexiones, oportunidades e impacto compartido. Las organizaciones participantes podrán involucrarse de maneras que se alineen con su misión, capacidad y prioridades.' : 'Building connections, opportunities, and shared impact together. Participating organizations will have opportunities to engage in ways that align with their mission, capacity, and priorities.'}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3845" data-mp="215a07dukjqel">
              {PARTICIPATION.map((item, i) => {
              const Icon = item.icon;
              return <motion.div key={item.title} initial={{
                opacity: 0,
                y: 16
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-60px'
              }} transition={{
                duration: 0.45,
                delay: Math.min(i, 3) * 0.06,
                ease: EASE
              }} className="rounded-xl border border-white/12 bg-white/[0.04] p-7 h-full flex flex-col" data-id="element-3846" data-mp="13foq4mub18qk" data-mpi={i}>
                    <Icon className="w-5 h-5 mb-4" style={{
                  color: SKY
                }} strokeWidth={1.7} data-id="element-3847" data-mp="2rgbyoqf0wq63" />
                    <h3 className="font-bebas text-xl tracking-[0.06em] uppercase mb-3" data-id="element-3848" data-mp="315ltie194mxw">
                      {isEs ? item.titleEs : item.title}
                    </h3>
                    <p className="font-nunito text-white/65 text-sm leading-relaxed" data-id="element-3849" data-mp="2wck8rped5t6e">
                      {isEs ? item.textEs : item.text}
                    </p>
                  </motion.div>;
            })}
            </div>

            <p className="font-nunito text-white/65 text-sm sm:text-base leading-relaxed max-w-3xl mt-10" data-id="element-3850" data-mp="3k2wpnprl7094">
              {isEs ? 'La Red está diseñada para ser flexible. Las organizaciones pueden participar en las oportunidades más relevantes para su trabajo y capacidad. Nuestra meta es facilitar que las organizaciones se conecten, colaboren, compartan recursos y persigan oportunidades juntas.' : 'The Network is designed to be flexible. Organizations may participate in the opportunities most relevant to their work and capacity. Our goal is to make it easier for organizations to connect, collaborate, share resources, and pursue opportunities together.'}
            </p>
          </div>
        </section>

        {/* 5 — How do I join */}
        <section style={{
        backgroundColor: NAVY_DEEP
      }} data-id="element-3851" data-mp="3dn6byfs8as44">
          <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20 lg:py-24" data-id="element-3852" data-mp="3dmehx9ea52fy">
            <div className="text-center mb-14" data-id="element-3853" data-mp="3ink8b0kgwdmb">
              <SectionLabel data-id="element-3854" data-mp="33pjjsrlx5427">
                {isEs ? '03 — Membresía' : '03 — Membership'}
              </SectionLabel>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95]" data-id="element-3855" data-mp="38o364t4fchv3">
                {isEs ? '¿Cómo Me Uno?' : 'How Do I Join?'}
              </h2>
            </div>

            <ol className="space-y-10" data-id="element-3856" data-mp="16xrqbcgb4wzr">
              {JOIN_STEPS.map((step, i) => <motion.li key={step.title} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.45,
              delay: Math.min(i, 3) * 0.06,
              ease: EASE
            }} className="relative pl-16 sm:pl-20 pb-10 border-b border-white/10 last:border-b-0 last:pb-0" data-id="element-3857" data-mp="15g2q87vlvvyu" data-mpi={i}>
                  <span className="absolute left-0 top-0 w-11 h-11 rounded-full flex items-center justify-center font-bebas text-lg tracking-wide text-white" style={{
                backgroundColor: i === 1 ? 'rgba(255,255,255,0.12)' : SKY
              }} aria-hidden="true" data-id="element-3858" data-mp="1j77lyyvus43w">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="font-bebas text-3xl tracking-[0.04em] mb-4" style={{
                color: SKY
              }} data-id="element-3859" data-mp="2248taqcvqgb0">
                    {isEs ? step.titleEs : step.title}
                  </h3>
                  <p className="font-nunito text-white/75 text-base leading-relaxed" data-id="element-3860" data-mp="3tt9u1q5idae3">
                    {isEs ? step.leadEs : step.lead}
                  </p>
                  {(isEs ? step.pointsEs : step.points).length > 0 ? <ul className="mt-5 space-y-3" data-id="element-3861" data-mp="7m1ux5l6lzqi">
                      {(isEs ? step.pointsEs : step.points).map(point => <li key={point.slice(0, 40)} className="flex gap-3" data-id="element-3862" data-mp="cjbg291jfp8j">
                          <CheckIcon className="w-4 h-4 mt-1 flex-shrink-0" style={{
                    color: RED
                  }} strokeWidth={2.5} data-id="element-3863" data-mp="hd68q1z4fejr" />
                          <span className="font-nunito text-white/65 text-sm leading-relaxed" data-id="element-3864" data-mp="vc5mgel3i915">
                            {point}
                          </span>
                        </li>)}
                    </ul> : null}
                  {i === 0 ? <a href={joinHref} className="inline-flex items-center gap-2 mt-5 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] transition-opacity hover:opacity-70" style={{
                color: SKY
              }} data-id="element-3865" data-mp="f9oo2d4g49nj">
                      <MailIcon className="w-3.5 h-3.5" data-id="element-3866" data-mp="3eosd20gjugwo" />
                      {isEs ? 'Solicitar información' : 'Request information'}
                    </a> : null}
                </motion.li>)}
            </ol>
          </div>
        </section>

        {/* 6 — Map */}
        <section className="border-y border-white/10" style={{
        backgroundColor: NAVY
      }} data-id="element-3867" data-mp="2zzw84c9shk0a">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-id="element-3868" data-mp="2vyu5rmkksru3">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-10" data-id="element-3869" data-mp="2ludocorg72o6">
              <div className="max-w-xl" data-id="element-3870" data-mp="2lsj5dfzqlkmq">
                <SectionLabel data-id="element-3871" data-mp="2qlc7dw3tmev4">
                  {isEs ? '04 — Alcance' : '04 — Reach'}
                </SectionLabel>
                <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95]" data-id="element-3872" data-mp="2bxirsbeqjccj">
                  {isEs ? 'Explore la Red' : 'Explore the Network'}
                </h2>
              </div>
              <p className="font-nunito text-white/45 text-xs uppercase tracking-[0.16em] max-w-xs" data-id="element-3873" data-mp="34lep9ic4p8ad">
                {isEs ? 'Seleccione un punto para ver la organización' : 'Select a point to view the organization'}
              </p>
            </div>

            <div className="rounded-xl overflow-hidden border border-white/12" style={{
            backgroundColor: NAVY_DEEP
          }} data-id="element-3874" data-mp="2zkh735r4ce8v">
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10" data-id="element-3875" data-mp="1o6dh0annja93">
                <span className="w-1.5 h-1.5 rounded-full" style={{
                backgroundColor: RED
              }} data-id="element-3876" data-mp="o257wyx0zagj" />
                <span className="font-nunito text-white/45 text-[10px] font-bold uppercase tracking-[0.22em]" data-id="element-3877" data-mp="szp072qk5nrh">
                  {isEs ? 'Mapa de la Red' : 'Network Map'}
                </span>
              </div>
              <div className="h-[440px] sm:h-[560px] w-full" data-id="element-3878" data-mp="jygzyf4hecur">
                <Suspense fallback={<div className="w-full h-full flex items-center justify-center" style={{
                backgroundColor: NAVY_DEEP
              }} data-id="element-3880" data-mp="59ksoka913v7">
                      <p className="font-nunito text-white/40 text-xs uppercase tracking-[0.2em]" data-id="element-3881" data-mp="3se0qv7hojj6n">
                        {isEs ? 'Cargando mapa…' : 'Loading map…'}
                      </p>
                    </div>} data-id="element-3879" data-mp="5b5n89wkcmt2">
                  <NetworkMap isEs={isEs} data-id="element-3882" data-mp="3nhadirx9f1ox" />
                </Suspense>
              </div>
            </div>
          </div>
        </section>

        {/* 7 — Roster */}
        <section style={{
        backgroundColor: NAVY_DEEP
      }} data-id="element-3883" data-mp="2yw5mim02jd8i">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-id="element-3884" data-mp="2yxp36dpmzqat">
            <div className="max-w-2xl mb-12" data-id="element-3885" data-mp="3du6bsx6fyqto">
              <SectionLabel data-id="element-3886" data-mp="3dr22xmi176wo">
                {isEs ? '05 — Organizaciones Participantes' : '05 — Participating Organizations'}
              </SectionLabel>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95] mb-6" data-id="element-3887" data-mp="3mrqf0bnv6x6x">
                {isEs ? 'Quiénes Forman la Red' : 'Who Makes Up the Network'}
              </h2>
              <p className="font-nunito text-white/65 text-base leading-relaxed" data-id="element-3888" data-mp="gcqhwfcenx8">
                {isEs ? 'Cada organización aporta una fortaleza distinta: educación, salud mental, artes, defensa legal, desarrollo juvenil. Esta es la base de la Red, y continúa creciendo.' : 'Each organization brings a distinct strength — education, mental health, the arts, legal advocacy, youth development. This is the foundation of the Network, and it continues to grow.'}
              </p>
            </div>

            <ul className="border-t border-white/10" data-id="element-3889" data-mp="mefndg4wmnr4">
              {SISTER_ORGANIZATIONS.map((org, i) => <motion.li key={org.name} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.45,
              delay: Math.min(i, 3) * 0.06,
              ease: EASE
            }} className="border-b border-white/10 transition-colors duration-200 hover:bg-white/[0.04]" data-id="element-3890" data-mp="r5xny330mmbk" data-mpi={i}>
                  <div className="py-8 grid lg:grid-cols-[3rem_minmax(0,1fr)_minmax(0,1.15fr)_auto] gap-x-8 gap-y-4 items-start px-2 sm:px-4" data-id="element-3891" data-mp="36y519as16v6a">
                    <span className="font-nunito text-white/30 text-[10px] font-bold tabular-nums tracking-[0.1em] pt-2" data-id="element-3892" data-mp="1t0sekjt7ozmt">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div data-id="element-3893" data-mp="1k8i4d2ibgu5k">
                      <p className="font-nunito text-[10px] font-bold uppercase tracking-[0.22em] mb-2.5" style={{
                    color: SKY
                  }} data-id="element-3894" data-mp="1k7p66pxfbcz1">
                        {isEs ? org.focusEs : org.focus}
                      </p>
                      <h3 className="font-bebas text-2xl sm:text-[1.7rem] tracking-[0.03em] leading-tight mb-2" data-id="element-3895" data-mp="27xjfwymg4lf2">
                        {org.name}
                      </h3>
                      <p className="inline-flex items-center gap-1.5 font-nunito text-white/50 text-xs uppercase tracking-[0.12em]" data-id="element-3896" data-mp="230sx43j9bbpy">
                        <MapPinIcon className="w-3 h-3 flex-shrink-0" style={{
                      color: RED
                    }} data-id="element-3897" data-mp="rk30nc4mxy9j" />
                        {org.location}
                      </p>
                    </div>
                    <p className="font-nunito text-white/65 text-sm leading-relaxed" data-id="element-3898" data-mp="2afl5imitmear">
                      {isEs ? org.descriptionEs : org.description}
                    </p>
                    <div className="flex flex-col gap-2.5 lg:items-end lg:text-right" data-id="element-3899" data-mp="21iko4zu65ggt">
                      {org.url ? <a href={org.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] transition-opacity whitespace-nowrap hover:opacity-70" style={{
                    color: SKY
                  }} data-id="element-3900" data-mp="26dgiw7qsihxm">
                          {isEs ? 'Sitio web' : 'Website'}
                          <ArrowUpRightIcon className="w-3.5 h-3.5" data-id="element-3901" data-mp="18u59wfiv326j" />
                        </a> : null}
                      <a href={mailto(`${isEs ? 'Contacto' : 'Contact'} — ${org.name}`)} className="inline-flex items-center gap-1.5 font-nunito text-white/50 text-[11px] font-bold uppercase tracking-[0.16em] hover:text-white transition-colors whitespace-nowrap" data-id="element-3902" data-mp="167e25dae2lpl">
                        <MailIcon className="w-3.5 h-3.5" data-id="element-3903" data-mp="mjn33r07odts" />
                        {isEs ? 'Contactar' : 'Contact'}
                      </a>
                    </div>
                  </div>
                </motion.li>)}
            </ul>
          </div>
        </section>

        {/* 8 — CTA */}
        <section className="relative overflow-hidden border-t border-white/10" style={{
        backgroundColor: NAVY
      }} data-id="element-3904" data-mp="riq6z84gge06">
          <div className="relative max-w-3xl mx-auto px-5 sm:px-8 py-20 lg:py-24 text-center" data-id="element-3905" data-mp="3rum6uyf2j2f">
            <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[1.02] mb-7" data-id="element-3906" data-mp="3uyp03te7zi0x">
              <span className="block" style={{
              color: SKY
            }} data-id="element-3907" data-mp="1yvivki26tzwe">
                {isEs ? 'Organizaciones Más Fuertes' : 'Stronger Organizations'}
              </span>
              <span className="block text-white" data-id="element-3908" data-mp="28mdlbwen7hml">
                {isEs ? 'Mayor Conexión' : 'Greater Connection'}
              </span>
              <span className="block" style={{
              color: RED
            }} data-id="element-3909" data-mp="2djwx8pnvbibd">
                {isEs ? '¡Impacto Binacional!' : 'Bi-National Impact!'}
              </span>
            </h2>
            <p className="font-nunito text-white/70 text-base sm:text-lg leading-relaxed mb-9" data-id="element-3910" data-mp="1kw139spoo4ia">
              {isEs ? 'Queremos construir esto con ustedes, no simplemente para ustedes. Si su organización sirve a las comunidades hondureñas o hondureño-americanas, nos encantaría conocerla.' : 'We want to build this with you, not simply for you. If your organization serves Honduran or Honduran-American communities, we would be honored to know you.'}
            </p>
            <a href={joinHref} className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-white font-bold text-xs uppercase tracking-[0.16em] transition-colors" style={{
            color: NAVY
          }} onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = RED;
            e.currentTarget.style.color = '#ffffff';
          }} onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = '#ffffff';
            e.currentTarget.style.color = NAVY;
          }} data-id="element-3911" data-mp="tpkb7ph04e9c">
              <MailIcon className="w-4 h-4" data-id="element-3912" data-mp="1xcvqsd8f7wj" />
              {CONTACT_EMAIL}
            </a>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10 py-8" style={{
      backgroundColor: NAVY_DEEP
    }} data-id="element-3913" data-mp="3t5h8ii7ppj2q">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 flex flex-wrap items-center justify-between gap-5" data-id="element-3914" data-mp="1w9tbqkvty0el">
          <span className="inline-flex bg-white rounded-md px-2.5 py-1.5" data-id="element-3915" data-mp="1rayfoh8uqfcj">
            <img src={NETWORK_LOGO} alt="Honduran American Association National Network" className="h-10 w-auto object-contain" data-id="element-3916" data-mp="1088lmpdmwe3i" />
          </span>
          <p className="font-nunito text-white/45 text-[10px] uppercase tracking-[0.22em]" data-id="element-3917" data-mp="qhdqevieu45x">
            {isEs ? 'Una iniciativa de la Asociación Hondureño-Americana' : 'An initiative of the Honduran-American Association'}
          </p>
        </div>
      </footer>
    </div>;
}